import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import NotificationContainer from '../Notifications/NotificationContainer';
import UserDebugPanel from '../Debug/UserDebugPanel';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import LoginScreen from '../Auth/LoginScreen';
import { useEffect } from 'react';

export default function MainLayout() {
  const { isAuthenticated, login, logout, user } = useAuth();
  const { data: userData, isLoading: isLoadingUser, error: userError } = useCurrentUser();

  const isDevelopmentMode = import.meta.env.VITE_DEVELOPMENT_MODE === 'true';

  useEffect(() => {
    console.log('🔍 Auth check on app load:', { isAuthenticated, user: user?.email });
  }, []);

  useEffect(() => {
    if (userData && isAuthenticated) {
      const token = import.meta.env.VITE_JWT_TOKEN || localStorage.getItem('chime_token');
      if (token) {
        login(token, {
          id: userData.id.toString(),
          name: userData.fullName || userData.username,
          email: userData.email,
          avatar: userData.profilePicture || undefined,
        });
      }
    }
  }, [userData, isAuthenticated, login]);

  if (!isAuthenticated) {
    return <LoginScreen isDevelopmentMode={isDevelopmentMode} />;
  }

  if (isLoadingUser && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (userError && isAuthenticated) {
    console.error('Failed to load user profile:', userError);
    // If there's an error loading user profile, show error message
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Profile</h3>
          <p className="text-sm text-gray-600 mb-4">
            {userError instanceof Error ? userError.message : 'Unable to load your profile. Please try logging in again.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Retry
            </button>
            <button
              onClick={logout}
              className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <NotificationContainer />
      <UserDebugPanel />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          <div className="hidden lg:block flex-shrink-0">
            <Sidebar />
          </div>
          <div className="flex-1 max-w-2xl">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}